import type { AppProps } from "next/app";
import { useEffect } from "react";
import { CookieConsentManager } from "@/components/cookie-consent";
import { BugsnagErrorBoundary } from "@/lib/bugsnag-client";
import { FirebaseAuthProvider } from "@/lib/firebase-auth-context";
import { BreadcrumbTitleProvider } from "@/lib/breadcrumb-title-context";
import { SnipcartCustomerLink } from "@/components/snipcart-customer-link";
import { StorefrontShell } from "@/components/storefront-shell";
import {
  buildAnalyticsContext,
  getGoogleTagManagerId,
  isAnalyticsEnabled,
  resolveAnalyticsSiteCode,
  resolveAnalyticsSource,
  setAnalyticsContext,
} from "@/lib/analytics";
import type { StorefrontPageProps } from "@/lib/storefront-shell-types";
import { fontVariableClassName } from "@/fonts";
import "@/app/globals.css";

export default function App({ Component, pageProps, router }: AppProps) {
  const isStandalonePaymentRoute = router.pathname.startsWith("/pay/stripe");
  const isStandaloneCheckoutRoute = router.pathname.startsWith("/app/checkout");
  const isStandaloneErrorRoute =
    router.pathname === "/404" || router.pathname === "/500";

  const content = <Component {...pageProps} />;
  const siteCode = resolveAnalyticsSiteCode(
    pageProps,
    typeof window === "undefined" ? undefined : window.location.host,
  );
  const analyticsContext = buildAnalyticsContext(
    siteCode,
    resolveAnalyticsSource(router.pathname),
  );
  const gtmId = isAnalyticsEnabled() ? getGoogleTagManagerId() : null;
  // UK PECR: GTM/GA4 must not load until the visitor accepts analytics
  // cookies. CookieConsentManager pushes Consent Mode v2 denied defaults,
  // gates the GTM tag on the stored choice, and owns the consent banner. The
  // banner is suppressed on standalone routes (payment / app-WebView checkout
  // / error pages) so a payment is never interrupted — GTM stays
  // consent-gated there regardless.
  const analyticsTag = (
    <CookieConsentManager
      gtmId={gtmId}
      analyticsContext={analyticsContext}
      siteCode={siteCode}
      allowBanner={
        !isStandalonePaymentRoute &&
        !isStandaloneCheckoutRoute &&
        !isStandaloneErrorRoute
      }
    />
  );

  useEffect(() => {
    setAnalyticsContext(analyticsContext);
  }, [analyticsContext]);

  if (isStandalonePaymentRoute || isStandaloneCheckoutRoute || isStandaloneErrorRoute) {
    return (
      <div className={fontVariableClassName}>
        {analyticsTag}
        <FirebaseAuthProvider>
          <SnipcartCustomerLink />
          <BugsnagErrorBoundary>{content}</BugsnagErrorBoundary>
        </FirebaseAuthProvider>
      </div>
    );
  }

  const storefrontPageProps = pageProps as StorefrontPageProps;

  return (
    <div className={fontVariableClassName}>
      {analyticsTag}
      <FirebaseAuthProvider>
        <BreadcrumbTitleProvider>
          <SnipcartCustomerLink />
          <BugsnagErrorBoundary>
            <StorefrontShell
              navigationItems={storefrontPageProps.navigationItems ?? []}
              shellData={storefrontPageProps.shellData}
              layoutVariant={storefrontPageProps.layoutVariant}
              siteCode={storefrontPageProps.siteCode}
            >
              {content}
            </StorefrontShell>
          </BugsnagErrorBoundary>
        </BreadcrumbTitleProvider>
      </FirebaseAuthProvider>
    </div>
  );
}
